import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import client from '../api/client';
import { buildRentersInsuranceRequestPayload } from '../utils/rentersInsurancePayload';
import { trackGenerateLead, trackFunnelStep, trackCarFunnelStep } from '../utils/analytics';
import {
  clearLeadSession,
  readLeadIdFromSession,
  wasLeadConversionFired,
} from '../utils/leadSession';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const showExistingAccountMsg =
    new URLSearchParams(location.search).get('existing') === '1';
  const showVerifiedMsg =
    new URLSearchParams(location.search).get('verified') === '1';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await client.post('/auth/login', { email, password });
      const { user, accessToken, refreshToken } = response.data;
      setAuth(user, accessToken, refreshToken);

      const replayWarnings: string[] = [];

      const insuranceFormData = sessionStorage.getItem('insuranceFormData');
      if (insuranceFormData) {
        try {
          const formDataObj = JSON.parse(insuranceFormData);
          const rentersPayload = buildRentersInsuranceRequestPayload(formDataObj) as Record<
            string,
            unknown
          >;
          const leadId = readLeadIdFromSession();
          if (leadId && !rentersPayload.leadId) rentersPayload.leadId = leadId;
          await client.post('/insurance/request', rentersPayload);
          if (!wasLeadConversionFired()) {
            await trackGenerateLead('renters');
          }
          sessionStorage.removeItem('insuranceFormData');
          sessionStorage.removeItem('insuranceType');
          sessionStorage.removeItem('zipCode');
          clearLeadSession();
        } catch (insuranceError) {
          console.error('Failed to submit insurance request after login:', insuranceError);
          replayWarnings.push(
            'Your saved renters insurance form could not be submitted. Please complete the form again from the qualify page.'
          );
        }
      }

      const carInsuranceFormData = sessionStorage.getItem('carInsuranceFormData');
      if (carInsuranceFormData) {
        try {
          const carPayload = JSON.parse(carInsuranceFormData) as Record<string, unknown>;
          const leadId = readLeadIdFromSession();
          if (leadId && !carPayload.leadId) carPayload.leadId = leadId;
          await client.post('/insurance/car-request', carPayload);
          trackCarFunnelStep('login_complete');
          if (!wasLeadConversionFired()) {
            await trackGenerateLead('car');
          }
          sessionStorage.removeItem('carInsuranceFormData');
          sessionStorage.removeItem('insuranceType');
          sessionStorage.removeItem('zipCode');
          clearLeadSession();
        } catch (carError) {
          console.error('Failed to submit car insurance request after login:', carError);
          replayWarnings.push(
            'Your saved car insurance form could not be submitted. Please complete the form again from the qualify page.'
          );
        }
      }

      const petInsuranceFormData = sessionStorage.getItem('petInsuranceFormData');
      if (petInsuranceFormData) {
        try {
          const petPayload = JSON.parse(petInsuranceFormData) as Record<string, unknown>;
          const leadId = readLeadIdFromSession();
          if (leadId && !petPayload.leadId) petPayload.leadId = leadId;
          await client.post('/insurance/pet-request', petPayload);
          trackFunnelStep('login_complete');
          if (!wasLeadConversionFired()) {
            await trackGenerateLead('pet');
          }
          sessionStorage.removeItem('petInsuranceFormData');
          sessionStorage.removeItem('insuranceType');
          sessionStorage.removeItem('zipCode');
          clearLeadSession();
        } catch (petError) {
          console.error('Failed to submit pet insurance request after login:', petError);
          replayWarnings.push(
            'Your saved pet insurance form could not be submitted. Please complete the form again from the qualify page.'
          );
        }
      }

      const homeInsuranceFormData = sessionStorage.getItem('homeInsuranceFormData');
      if (homeInsuranceFormData) {
        try {
          const homePayload = JSON.parse(homeInsuranceFormData) as Record<string, unknown>;
          const leadId = readLeadIdFromSession();
          if (leadId && !homePayload.leadId) homePayload.leadId = leadId;
          await client.post('/insurance/home-request', homePayload);
          if (!wasLeadConversionFired()) {
            await trackGenerateLead('home');
          }
          sessionStorage.removeItem('homeInsuranceFormData');
          sessionStorage.removeItem('insuranceType');
          sessionStorage.removeItem('zipCode');
          clearLeadSession();
        } catch (homeError) {
          console.error('Failed to submit home insurance request after login:', homeError);
          replayWarnings.push(
            'Your saved home insurance form could not be submitted. Please complete the form again from the qualify page.'
          );
        }
      }

      if (replayWarnings.length > 0) {
        sessionStorage.setItem('insuranceReplayWarning', replayWarnings.join(' '));
      }

      const redirect = sessionStorage.getItem('redirectAfterSignup');
      sessionStorage.removeItem('redirectAfterSignup');
      navigate(redirect && redirect.startsWith('/') ? redirect : '/insurance/requests');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        setError('Login failed. Please check your email and password and try again.');
      } else {
        setError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '400px', margin: '2rem auto' }}>
      <div className="card">
        <div className="card-header">Sign In</div>
        <form onSubmit={handleSubmit}>
          {showVerifiedMsg && (
            <div className="alert alert-success">
              Your email has been verified. You can now sign in.
            </div>
          )}
          {showExistingAccountMsg && (
            <div className="alert alert-success">
              An account with this email already exists. Please sign in below.
            </div>
          )}
          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#4B5563' }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: 'var(--color-green)', fontWeight: '600' }}>
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
